import { readItemField } from './item-field-reader';
import { fromBaseRecord, FromBaseStageMissing } from './from-base-context';
import { isPhpNumeric, phpJsonFloat, roundPhpDecimal } from './php-decimal-values';

/** Measured mass and authored placement bounds are independent: a bounding box
 * never substitutes for solid volume. Other profile constraints remain a named gap. */
export function readFromBaseDerivedMeshMass(raw: unknown): { volume: number; measurement: string } | null {
    if (raw == null || raw === '') return null;
    let profile: unknown = raw;
    if (typeof raw === 'string') { try { profile = JSON.parse(raw); } catch { throw new FromBaseStageMissing('derived-mesh-weight', 'invalid-profile'); } }
    if (!fromBaseRecord(profile) || readItemField(profile, 'schema') !== 'ft.mesh-generation-profile.v1')
        throw new FromBaseStageMissing('derived-mesh-weight', 'profile-schema');
    if (Object.keys(profile).some(key => !['schema', 'mass', 'authoredBoundsM'].includes(key)))
        throw new FromBaseStageMissing('derived-mesh-weight', 'profile-constraints');
    if ('authoredBoundsM' in profile) {
        const bounds = profile.authoredBoundsM;
        if (!Array.isArray(bounds) || bounds.length !== 3 || !bounds.every(axis =>
            isPhpNumeric(axis) && Number.isFinite(Number(axis)) && Number(axis) >= 0 && Number(axis) <= 10000))
            throw new FromBaseStageMissing('derived-mesh-weight', 'profile:authoredBoundsM');
    }
    if (!fromBaseRecord(profile.mass)) return null;
    const mass = profile.mass;
    const token = (name: string): string => {
        const value = readItemField(mass, name);
        if (typeof value !== 'string' || !/^[a-z0-9][a-z0-9_-]{0,63}$/.test(value.trim().toLowerCase()))
            throw new FromBaseStageMissing('derived-mesh-weight', `mass:${name}`);
        return value.trim().toLowerCase();
    };
    if (Object.keys(mass).some(key => !['construction', 'solidVolumeM3', 'surfaceAreaM2', 'openEdgeRatio', 'measurementStatus', 'toolVersion'].includes(key)))
        throw new FromBaseStageMissing('derived-mesh-weight', 'mass-keys');
    const construction = token('construction'), measurement = token('measurementStatus');
    if (!['solid', 'hollow', 'unknown'].includes(construction)) throw new FromBaseStageMissing('derived-mesh-weight', 'mass:construction');
    if ('toolVersion' in mass) token('toolVersion');
    for (const [name, max] of [['solidVolumeM3', 100000], ['surfaceAreaM2', 1000000], ['openEdgeRatio', 1]] as const) {
        const value = readItemField(mass, name);
        if (name in mass && (!isPhpNumeric(value) || !Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > max))
            throw new FromBaseStageMissing('derived-mesh-weight', `mass:${name}`);
    }
    const volume = roundPhpDecimal(phpJsonFloat(readItemField(mass, 'solidVolumeM3') ?? 0), 5);
    return construction === 'solid' && volume > 0 && ['closed_manifold', 'near_closed', 'shell_low_confidence'].includes(measurement)
        ? { volume, measurement } : null;
}
